// JSON fetch wrapper. Tracks the CSRF token via the X-CSRF-Token response
// header from prior safe requests (the __Host-csrf cookie is HttpOnly per
// the middleware comment in internal/csrf/middleware.go, so document.cookie
// can't read it). Falls back to a document.cookie read for tests that mint
// non-HttpOnly cookies. All API endpoints expect JSON; cursor params (if
// any) ride in the request body (POST-for-complex-read) per WAF Rule 1.

import { getAccessToken, refreshAccessToken, setAccessToken } from './auth';
import { handleCompromiseSignal } from './compromise';

const CSRF_COOKIE = '__Host-csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const COMPROMISE_HEADER = 'X-Compromise-Signal';

let cachedCsrfToken: string | null = null;

function csrfToken(): string {
  if (cachedCsrfToken) return cachedCsrfToken;
  // Fallback for environments where the cookie is reachable from JS
  // (older dev http config, tests). Returns empty when the cookie is
  // HttpOnly — that's the production case and the response-header
  // capture below is what we actually rely on.
  const m = document.cookie.match(new RegExp('(?:^|; )' + CSRF_COOKIE.replace(/\$/g, '\\$') + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : '';
}

type ApiOpts = {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  return apiJSON<T>(path, opts, true);
}

async function apiJSON<T>(path: string, opts: ApiOpts, allowRetry: boolean): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  applyAuth(headers, method);
  if (opts.idempotencyKey) {
    headers['Idempotency-Key'] = opts.idempotencyKey;
  }
  // Caller-supplied headers last so they can set request-specific values
  // (e.g. X-Device-Class on refresh) without overriding the auth/CSRF wiring.
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) headers[k] = v;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
  captureResponseMeta(res);
  const text = await res.text();
  const json = text ? safeJSON(text) : null;
  if (!res.ok) {
    if (allowRetry && shouldRecover(res, path) && (await recoverFrom401())) {
      // Refresh succeeded — replay the original request once with the new token.
      return apiJSON<T>(path, opts, false);
    }
    throw new ApiError(res.status, json, `api ${method} ${path}: ${res.status}`);
  }
  return json as T;
}

function safeJSON(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

// applyAuth attaches the CSRF token (on unsafe methods) and the short-lived JWT
// (Authorization) shared by every request. vfco-auth-package's Protected
// middleware reads the bearer token; the auth-package's own public endpoints
// (magic-link / verify / refresh) ignore it.
function applyAuth(headers: Record<string, string>, method: string): void {
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const token = csrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }
  const access = getAccessToken();
  if (access) headers['Authorization'] = 'Bearer ' + access;
}

// captureResponseMeta harvests the two out-of-band signals every response
// carries: the rotating CSRF token (echoed so JS can submit it on the next
// unsafe method without reading the HttpOnly cookie) and the compromised-device
// flag (which triggers the offline wipe + redirect centrally, so any endpoint's
// signal is honoured, not just /api/me).
function captureResponseMeta(res: Response): void {
  const headerToken = res.headers.get(CSRF_HEADER);
  if (headerToken) cachedCsrfToken = headerToken;
  if (res.headers.get(COMPROMISE_HEADER) === 'true') {
    handleCompromiseSignal();
  }
}

// shouldRecover reports whether a failed response is an ordinary session-expiry
// 401 the transport can recover from: a 401 that is NOT a compromise signal (that
// path drives its own offline-wipe + redirect, #204) and is NOT itself an
// /api/auth/ call (so the refresh can never recurse on its own 401). Everything
// else — a real 403, a 4xx/5xx, an authed-endpoint failure — throws as before.
function shouldRecover(res: Response, path: string): boolean {
  return (
    res.status === 401 &&
    res.headers.get(COMPROMISE_HEADER) !== 'true' &&
    !path.startsWith('/api/auth/')
  );
}

// recoverFrom401 attempts a single silent token refresh (single-flight, in
// auth.ts) so a stale access token is transparently renewed and the request
// retried once. If the refresh fails the session is genuinely gone: clear the
// token and hard-navigate to /login?next=<current> — matching the auth guard's
// shape so returnTo restores the destination after login (#157) — so a page never
// wedges on a permanent loading state. Returns whether the caller should retry.
async function recoverFrom401(): Promise<boolean> {
  if (await refreshAccessToken()) return true;
  setAccessToken(null);
  location.assign('/login?next=' + encodeURIComponent(location.pathname + location.search));
  return false;
}

export type BlobResponse = { blob: Blob; filename: string | null };

// apiBlob is the transport for endpoints that stream a binary/text attachment
// rather than JSON — the audited CSV export (POST /api/leads/export) is the first.
// It shares api()'s auth + CSRF + compromise wiring but returns the raw Blob plus
// the download filename parsed from Content-Disposition. Error responses are still
// JSON (the AuthError shape), so a non-2xx throws the same ApiError as api() with
// the parsed body — callers handle 401/403/503 identically.
export function apiBlob(path: string, opts: ApiOpts & { accept?: string } = {}): Promise<BlobResponse> {
  return apiBlobInner(path, opts, true);
}

async function apiBlobInner(
  path: string,
  opts: ApiOpts & { accept?: string },
  allowRetry: boolean,
): Promise<BlobResponse> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: opts.accept ?? 'application/octet-stream',
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  applyAuth(headers, method);
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) headers[k] = v;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
  captureResponseMeta(res);
  if (!res.ok) {
    if (allowRetry && shouldRecover(res, path) && (await recoverFrom401())) {
      return apiBlobInner(path, opts, false);
    }
    const text = await res.text();
    throw new ApiError(res.status, text ? safeJSON(text) : null, `api ${method} ${path}: ${res.status}`);
  }
  const blob = await res.blob();
  return { blob, filename: filenameFromDisposition(res.headers.get('Content-Disposition')) };
}

// filenameFromDisposition pulls the download filename out of a Content-Disposition
// header (`attachment; filename="leads-export-….csv"`), or null when absent.
function filenameFromDisposition(cd: string | null): string | null {
  if (!cd) return null;
  const m = /filename="?([^";]+)"?/.exec(cd);
  return m ? m[1] : null;
}
